import { toast, ToastContainer, ToastOptions } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import moment from 'moment';
import dayjs from 'dayjs';
import { Divider } from '@mantine/core';
import { DateField } from '../../components/DateField/DateField';
import Layout from '../../components/Layout';
import { Title } from '../../components/Title';
import useDailyCapacity from '../../hooks/common/useDailyCapacity';
import TextField from '../../components/TextField/TextField';
import LoadingPage from '../LoadingPage';
import ErrorPage from '../ErrorPage';
import { RequireAuth } from '../../components/AuthWrapper/RequireAuth';
import setTitle from '../../utils/title';
import useGetWeeklyCounts from '../../hooks/useGetWeeklyCountsRequest';
import AvailableAppointmentsMenu from './AvailableAppointmentsMenu';
import NumberOfAbsencesInput from './NumberOfAbsencesInput';
import useUserAbsences from '../../hooks/common/useUserAbsences';
import useUserNextAvailableAppointment from '../../hooks/common/useUserNextAvailableAppointment';
import CommonButton from '../../components/CommonButton';

export enum Mode {
  edit = 'edit',
  initial = 'initial',
}

const toastOptions: ToastOptions = {
  position: 'bottom-right',
};

interface ISetCapacityPageProps {
  mode: Mode;
}

function getMonday(d: Date) {
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
  return new Date(d.setDate(diff));
}

function SetCapacityPageUnsecured() {
  const navigate = useNavigate();
  const thisMonthYear = dayjs().format('MMMM YYYY');

  const [validated, setValidated] = useState<boolean | null>(null);
  const [date, setDate] = useState<Date>(new Date());
  const [capacityCountTitle, setCapacityCountTitle] = useState('');
  const [appointmentsCountTitle, setAppointmentsCountTitle] = useState('');
  const [capacityValue, setCapacityValue] = useState<string>('');

  useEffect(() => {
    const mondayDate = getMonday(new Date(date));
    const momentMondayDate = moment(mondayDate);
    const momentSundayDate = momentMondayDate.clone().add(6, 'days');

    const period = `${momentMondayDate.format(
      'DD/MM'
    )} - ${momentSundayDate.format('DD/MM')}`;

    setCapacityCountTitle(`Total Capacity For Week: ${period}`);
    setAppointmentsCountTitle(`Total Appointments For Week: ${period}`);
  }, [date]);

  const { loading, error, dailyCapacity, addDailyCapacity, addingCapacity } =
    useDailyCapacity(moment(date).format('YYYY-MM-DD'));

  const {
    loading: weeklyCountLoading,
    error: weeklyCountError,
    counts,
  } = useGetWeeklyCounts(date);

  const {
    loading: loadingNextAvailableAppointment,
    error: nextAvailableAppointmentError,
    nextAvailableAppointment,
    addUserNextAvailableAppointment,
    refetch: refetchUserNextAvailable,
  } = useUserNextAvailableAppointment();

  const {
    loading: loadingUserAbsences,
    error: userAbsencesError,
    addUserAbsences,
    userAbsences,
    refetch: refetchUserAbsences,
  } = useUserAbsences();

  const validateNumberInput = () =>
    /^(?:[1-9]|[12][0-9]|30)$/.test(capacityValue);

  const onBtnClick = () => {
    if (!validateNumberInput()) {
      setValidated(false);
      return;
    }
    setValidated(true);
    addDailyCapacity(Number(capacityValue))
      .then(() => {
        navigate('/register');
      })
      .catch(() => {
        toast.error('There was a problem adding the capacity', toastOptions);
      });
  };

  if (
    error ||
    weeklyCountError ||
    nextAvailableAppointmentError ||
    userAbsencesError
  )
    return <ErrorPage layout />;

  if (
    loading ||
    weeklyCountLoading ||
    !counts ||
    loadingNextAvailableAppointment ||
    loadingUserAbsences ||
    addingCapacity
  )
    return <LoadingPage layout />;

  return (
    <Layout>
      <div className="flex flex-col gap-10">
        <Title text="Welcome to the Capacity Page" />
        <div className="grid grid-cols-2 gap-4">
          <AvailableAppointmentsMenu
            addUserNextAvailableAppointment={addUserNextAvailableAppointment}
            nextAvailableAppointment={nextAvailableAppointment}
            refetch={refetchUserNextAvailable}
            thisMonthYear={thisMonthYear}
          />
          <NumberOfAbsencesInput
            addUserAbsences={addUserAbsences}
            userAbsences={userAbsences}
            refetch={refetchUserAbsences}
            thisMonthYear={thisMonthYear}
          />
        </div>
        <Divider />
        <div className="grid grid-cols-2 gap-4">
          <TextField
            text={counts.capacityCount.toString()}
            label={capacityCountTitle}
            disabled
          />
          <TextField
            text={counts.appointmentCount.toString()}
            label={appointmentsCountTitle}
            disabled
          />
        </div>
        <div className=" bg-white rounded-md shadow-md border-[1px] mb-8">
          <p className="pt-6 pb-4 text-2xl font-semibold text-center">
            Enter Daily Capacity
          </p>
          <div className="grid grid-cols-2 gap-6 p-8">
            <div>
              <DateField
                label="Selected Date"
                maxDate={moment().toDate()}
                className="text-xl font-semibold"
                date={date}
                setDate={setDate}
              />
            </div>
            <div>
              <TextField
                text={capacityValue}
                setText={setCapacityValue}
                placeholderText={dailyCapacity?.capacityCount.toString() ?? '0'}
                pattern="^[0-9]*$"
                textAlign="center"
                label="Capacity"
              />
            </div>
          </div>
          <div className="flex flex-col items-center gap-4 my-6">
            <CommonButton
              className="p-4 font-semibold text-white bg-blue-500 border border-gray-400 rounded-md"
              onClick={onBtnClick}
              text="Confirm Slots"
              variant="filled"
            />
            {validated && (
              <p className="font-semibold text-green-500">Submitted</p>
            )}
            {validated === false && (
              <p className="p-1 text-red-600">
                Please enter a number between 1-30.
              </p>
            )}
          </div>
        </div>
      </div>
      <ToastContainer />
    </Layout>
  );
}

function SetCapacityPage({ mode }: ISetCapacityPageProps) {
  setTitle(mode === Mode.edit ? 'Edit Capacity' : 'Set Capacity');
  return (
    <RequireAuth>
      <SetCapacityPageUnsecured />
    </RequireAuth>
  );
}

export default SetCapacityPage;
